import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BgImage from "../../assets/img/illustrations/signin.svg";
import { useAuth } from "../../store/AuthContext";
import { FormProvider, useForm } from "react-hook-form";
import { Textbox } from "../../components/Textbox";
import { sendError, sendSuccess } from "../../helpers/Helper";
import { API_URL_ENDPOINT } from "../../appsetting";
import axios from "axios";
import { Routess } from "../../Routes/routes";
import { theme } from "../../constants/theme";

// Book SVG Icon component
const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke={theme.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z" stroke={theme.colors.secondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const styles = {
  // Styles that are hard to do with simple Tailwind classes or need dynamic values
  backgroundPattern: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    opacity: 0.6,
  },
  floatingBook1: {
    position: 'absolute',
    width: '80px',
    height: '100px',
    background: 'linear-gradient(135deg, #d4a574 0%, #c49a6c 100%)',
    borderRadius: '4px 12px 12px 4px',
    top: '15%',
    left: '10%',
    transform: 'rotate(-15deg)',
    animation: 'floatBook 8s ease-in-out infinite',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  floatingBook2: {
    position: 'absolute',
    width: '60px',
    height: '80px',
    background: theme.gradients.secondary,
    borderRadius: '4px 10px 10px 4px',
    top: '60%',
    left: '5%',
    transform: 'rotate(10deg)',
    animation: 'floatBook 10s ease-in-out infinite reverse',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  floatingBook3: {
    position: 'absolute',
    width: '70px',
    height: '90px',
    background: theme.gradients.main,
    borderRadius: '4px 10px 10px 4px',
    top: '20%',
    right: '8%',
    transform: 'rotate(20deg)',
    animation: 'floatBook 9s ease-in-out infinite',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  floatingBook4: {
    position: 'absolute',
    width: '50px',
    height: '65px',
    background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    borderRadius: '4px 8px 8px 4px',
    bottom: '20%',
    right: '12%',
    transform: 'rotate(-8deg)',
    animation: 'floatBook 7s ease-in-out infinite reverse',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  bookSpine: {
    position: 'absolute',
    left: '0',
    top: '0',
    bottom: '0',
    width: '8px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '4px 0 0 4px',
  },
};

// Add keyframes for animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes floatBook {
    0%, 100% { transform: translateY(0px) rotate(var(--rotation, -15deg)); }
    50% { transform: translateY(-15px) rotate(calc(var(--rotation, -15deg) + 3deg)); }
  }
`;
document.head.appendChild(styleSheet);


export const SignIn = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const methods = useForm();
  const { register, handleSubmit, reset, formState: { errors, isSubmitting }, watch } = methods;

  const onSubmit = async (param) => {
    try {
      const response = await axios.post(API_URL_ENDPOINT.Login, param, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data?.status) {
        console.log("response", response)
        await login(response.data?.data?.token)
        reset();
        sendSuccess(response.data?.message);
        navigate("/");
      } else {
        sendError(response.data?.message || "Something went wrong");
      }
    } catch (ex) {
      sendError(ex.response?.data?.message || ex.message || "Something went wrong");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-dark p-5 relative overflow-hidden">
      {/* Floating books background decoration */}
      <div style={styles.backgroundPattern}>
        <div style={styles.floatingBook1}><div style={styles.bookSpine}></div></div>
        <div style={styles.floatingBook2}><div style={styles.bookSpine}></div></div>
        <div style={styles.floatingBook3}><div style={styles.bookSpine}></div></div>
        <div style={styles.floatingBook4}><div style={styles.bookSpine}></div></div>
      </div>

      {/* Login Card */}
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-10 max-w-md w-full relative z-10 transform transition-all hover:-translate-y-1 duration-300">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <img
            className="w-24 h-24 rounded-full border-4 border-transparent mx-auto shadow-md transition-transform hover:scale-105"
            style={{
              background: `linear-gradient(white, white) padding-box, ${theme.gradients.main} border-box`,
            }}
            src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhxAH8Z0Jut-2sc95LBR0EXlGLMWrD_3z9wfzB1yPmlNNe_YQHVbZgX6EWXu24dFyEID8mzm8EsO8X4-0PmETpzGP_uoXP7UtOxZqQlJzRjpjBurWXGYuxa4WTzvvp2ojvzVbAA4rLzh5g/s1600/unnamed_22.png"
            alt="Jambhani Darshan Logo"
          />
          <div className="w-16 h-1 mx-auto my-4 rounded-full bg-gradient-to-r from-accent to-primary" style={{ height: '4px' }}></div>
          <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-accent to-primary mt-4 mb-2">
            Jambhani Darshan
          </h3>
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <BookIcon />
            <span>e-Library Management Portal</span>
          </p>
        </div>

        {/* Login Form */}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-6">
              <Textbox
                Label="Your Email or Mobile"
                name="emailOrMobile"
                Placeholder="Enter Your Email or Mobile Number"
                validationRules={{ required: "Email is required" }}
                register={register}
                errors={errors}
                watch={watch}
              />
            </div>
            <div className="mb-6">
              <Textbox
                Label="Your Password"
                Type="password"
                name="password"
                Placeholder="Enter Your Password"
                validationRules={{ required: "Password is required" }}
                register={register}
                errors={errors}
                watch={watch}
              />
            </div>
            <button
              className={`w-full py-3.5 px-6 text-base font-bold text-white bg-gradient-to-r from-accent to-primary border-none rounded-xl cursor-pointer transition-all duration-300 shadow-md relative overflow-hidden hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed`}
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In to Library"
              )}
            </button>
          </form>
        </FormProvider>

        {/* Welcome message */}
        <div className="text-xs text-gray-500 text-center mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          📚 Access thousands of digital books, manage your collection, and explore new titles
        </div>
      </div>
    </main>
  );
};
